import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users, Search, X, Crown, Loader2, Save, Mail, Phone, Globe, Award, ClipboardList } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/**
 * Admin Clients CRM (v3.9.6).
 *
 * Standalone customer database — auto-populated when bookings are
 * created / completed. Provides a searchable table + right-side drawer
 * with full customer overview, manual VIP override, notes editor, and
 * a linked booking history.
 */
const TIER_META = {
  Standard: { color: "bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200", label: "Standard" },
  Silver:   { color: "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100",       label: "Silver" },
  Gold:     { color: "bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100",       label: "Gold" },
  VIP:      { color: "bg-purple-200 text-purple-900 dark:bg-purple-800 dark:text-purple-100",   label: "VIP" },
};

function TierBadge({ tier, override }) {
  const meta = TIER_META[tier] || TIER_META.Standard;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium tracking-wide uppercase px-2 py-0.5 rounded ${meta.color}`}
      data-testid={`tier-badge-${tier}`}
      title={override ? "Manual admin override" : "Auto-calculated from booking count"}
    >
      {tier === "VIP" ? <Crown size={10} /> : <Award size={10} />}
      {meta.label}
      {override && <span className="text-[8px] opacity-70">·M</span>}
    </span>
  );
}

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/clients")
      .then((r) => setClients(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (tierFilter !== "all" && c.tier !== tierFilter) return false;
      if (!s) return true;
      return [c.full_name, c.email, c.phone].some((v) => (v || "").toLowerCase().includes(s));
    });
  }, [clients, search, tierFilter]);

  const stats = useMemo(() => {
    const counts = { Standard: 0, Silver: 0, Gold: 0, VIP: 0 };
    for (const c of clients) counts[c.tier || "Standard"]++;
    return counts;
  }, [clients]);

  return (
    <div className="max-w-7xl px-6 lg:px-10 pt-6 lg:pt-10 pb-12" data-testid="admin-clients-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">CRM</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2 flex items-center gap-3">
            <Users size={28} /> Clients
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-2xl">
            Every customer who books through <em>/book</em> or is added manually. Loyalty tier auto-ladders on completed bookings; upgrade to VIP manually for house guests.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {["Standard", "Silver", "Gold", "VIP"].map((t) => (
          <button
            key={t}
            onClick={() => setTierFilter((prev) => (prev === t ? "all" : t))}
            className={`text-left p-4 rounded border transition-all ${
              tierFilter === t
                ? "border-black dark:border-white bg-neutral-50 dark:bg-neutral-900"
                : "border-black/10 dark:border-white/10 hover:border-black dark:hover:border-white"
            } bg-white dark:bg-[#1F2937]`}
            data-testid={`tier-filter-${t}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <TierBadge tier={t} />
            </div>
            <p className="font-display text-2xl tabular-nums">{stats[t]}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {stats[t] === 1 ? "customer" : "customers"}
            </p>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 border border-black/10 dark:border-white/10 bg-white dark:bg-[#1F2937] px-3 py-2 rounded w-full md:w-96">
          <Search size={14} className="text-neutral-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or phone"
            className="bg-transparent outline-none text-sm flex-1"
            data-testid="clients-search"
          />
        </div>
        {tierFilter !== "all" && (
          <button
            type="button"
            onClick={() => setTierFilter("all")}
            className="text-xs px-3 py-1.5 rounded border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"
            data-testid="clients-clear-filter"
          >
            Clear tier filter
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 border-b border-black/8 dark:border-white/10 bg-[#FAF9F6] dark:bg-[#111827]">
            <tr>
              <th className="py-3 px-4">Customer</th>
              <th className="py-3 px-4">Contact</th>
              <th className="py-3 px-4">Tier</th>
              <th className="py-3 px-4 text-right">Bookings</th>
              <th className="py-3 px-4 text-right">Revenue</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="py-8 text-center text-neutral-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-neutral-500" data-testid="clients-empty">
                No clients match the current filters.
              </td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-black/5 dark:border-white/5 hover:bg-neutral-50 dark:hover:bg-neutral-900/40 cursor-pointer"
                  onClick={() => setSelectedId(c.id)} data-testid={`client-row-${c.id}`}>
                <td className="py-3 px-4">
                  <div className="font-medium">{c.full_name || "—"}</div>
                  {c.country && <div className="text-xs text-neutral-500 dark:text-neutral-400">{c.country}</div>}
                </td>
                <td className="py-3 px-4">
                  <div className="text-xs">{c.email}</div>
                  {c.phone && <div className="text-xs text-neutral-500 dark:text-neutral-400">{c.phone}</div>}
                </td>
                <td className="py-3 px-4"><TierBadge tier={c.tier || "Standard"} override={c.tier_overridden} /></td>
                <td className="py-3 px-4 text-right tabular-nums">{c.total_bookings || 0}</td>
                <td className="py-3 px-4 text-right tabular-nums">€{Number(c.total_revenue || 0).toFixed(0)}</td>
                <td className="py-3 px-4 text-right">
                  <button className="text-xs underline text-neutral-500 hover:text-black dark:hover:text-white" data-testid={`open-client-${c.id}`}>
                    Open →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <ClientDrawer clientId={selectedId} onClose={() => setSelectedId(null)} onSaved={load} />
      )}
    </div>
  );
}

function ClientDrawer({ clientId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [tier, setTier] = useState("Standard");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/clients/${clientId}`)
      .then((r) => {
        setData(r.data);
        setNotes(r.data.client?.notes || "");
        setTier(r.data.client?.tier || "Standard");
      })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [clientId]);

  const saveNotes = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/clients/${clientId}/notes`, { notes });
      toast.success("Notes saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveTier = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/clients/${clientId}/tier`, { tier });
      toast.success(`Tier set to ${tier}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose} data-testid="client-drawer">
      <div className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] w-full max-w-2xl h-full overflow-y-auto shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-black/10 dark:border-white/10 flex items-center justify-between sticky top-0 bg-white dark:bg-[#1F2937] z-10">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Client</p>
            <h2 className="font-display text-2xl mt-1">{data?.client?.full_name || "…"}</h2>
          </div>
          <button onClick={onClose} aria-label="Close drawer" data-testid="close-drawer" className="p-2 hover:bg-black/5 rounded">
            <X size={18} />
          </button>
        </div>

        {loading && <div className="p-8 text-center text-sm text-neutral-500"><Loader2 size={16} className="animate-spin mx-auto" /></div>}

        {data && (
          <div className="p-6 space-y-6">
            <section className="space-y-2 text-sm" data-testid="client-overview">
              <div className="flex items-center gap-2"><Mail size={14} className="text-neutral-400" /> {data.client.email}</div>
              {data.client.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-neutral-400" /> {data.client.phone}</div>}
              {data.client.country && <div className="flex items-center gap-2"><Globe size={14} className="text-neutral-400" /> {data.client.country}</div>}
              {data.client.driver_license && <div className="text-xs text-neutral-500 mt-2">Driver license: <span className="font-mono">{data.client.driver_license}</span></div>}
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="p-3 rounded border border-black/10 dark:border-white/10">
                  <p className="text-[10px] uppercase tracking-widest text-neutral-500">Total bookings</p>
                  <p className="font-display text-2xl tabular-nums" data-testid="client-total-bookings">{data.client.total_bookings || 0}</p>
                </div>
                <div className="p-3 rounded border border-black/10 dark:border-white/10">
                  <p className="text-[10px] uppercase tracking-widest text-neutral-500">Total revenue</p>
                  <p className="font-display text-2xl tabular-nums" data-testid="client-total-revenue">€{Number(data.client.total_revenue || 0).toFixed(0)}</p>
                </div>
              </div>
            </section>

            <section className="space-y-3" data-testid="client-tier-editor">
              <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Loyalty tier</p>
              <div className="flex items-center gap-2">
                <TierBadge tier={data.client.tier || "Standard"} override={data.client.tier_overridden} />
                {data.client.tier_overridden && (
                  <span className="text-[10px] text-amber-700 dark:text-amber-400">Manual override active</span>
                )}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <select
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 text-sm outline-none"
                  data-testid="client-tier-select"
                >
                  <option value="Standard">Standard (auto)</option>
                  <option value="Silver">Silver</option>
                  <option value="Gold">Gold</option>
                  <option value="VIP">VIP</option>
                </select>
                <button
                  onClick={saveTier}
                  disabled={busy || tier === (data.client.tier || "Standard")}
                  className="bg-black text-white dark:bg-white dark:text-black text-xs px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-1"
                  data-testid="client-save-tier"
                >
                  <Save size={12} /> Apply tier
                </button>
              </div>
              <p className="text-[10px] text-neutral-500 dark:text-neutral-400">
                Standard/Silver/Gold auto-calculate from booking count (0-2 / 3-4 / 5+). VIP is manual and gets 15% off — set back to Standard to resume auto ladder.
              </p>
            </section>

            <section className="space-y-2" data-testid="client-notes-editor">
              <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Notes</p>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full bg-transparent border border-black/15 dark:border-white/15 rounded p-2 text-sm outline-none focus:border-black dark:focus:border-white"
                placeholder="Internal notes — dietary preferences, VIP requests, etc."
                data-testid="client-notes-input"
              />
              <button
                onClick={saveNotes}
                disabled={busy}
                className="bg-black text-white dark:bg-white dark:text-black text-xs px-3 py-1.5 rounded hover:opacity-90 inline-flex items-center gap-1"
                data-testid="client-save-notes"
              >
                <Save size={12} /> Save notes
              </button>
            </section>

            <section data-testid="client-bookings-list">
              <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-3 flex items-center gap-1">
                <ClipboardList size={12} /> Booking history ({data.bookings.length})
              </p>
              {data.bookings.length === 0 && (
                <p className="text-xs text-neutral-500 italic">No bookings yet.</p>
              )}
              <div className="space-y-2">
                {data.bookings.map((b) => (
                  <div key={b.id} className="p-3 rounded border border-black/10 dark:border-white/10 text-sm" data-testid={`booking-history-${b.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{b.vehicle_snapshot?.name || b.vehicle_snapshot?.brand || "Vehicle"}</p>
                        <p className="text-xs text-neutral-500">
                          {b.pickup_date} → {b.return_date} · {b.days} day{b.days === 1 ? "" : "s"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold tabular-nums">€{Number(b.total || 0).toFixed(0)}</p>
                        <p className={`text-[10px] uppercase tracking-widest ${
                          b.status === "completed" ? "text-emerald-700 dark:text-emerald-400"
                          : b.status === "cancelled" ? "text-rose-600 dark:text-rose-400"
                          : "text-neutral-500"
                        }`}>{b.status}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
